import { useState, useCallback } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { StripeConnectService, StripeConnectStatus } from "@/utils";

export interface UseStripeConnectReturn {
  status: StripeConnectStatus | null;
  loading: boolean;
  loadingStart: boolean;
  loadingDisconnect: boolean;
  lastError: string | null;
  clearError: () => void;
  loadStatus: (refresh?: boolean) => Promise<StripeConnectStatus | null>;
  start: (returnUrl: string) => Promise<string | null>;
  complete: (code: string, state: string) => Promise<StripeConnectStatus | null>;
  disconnect: () => Promise<StripeConnectStatus | null>;
}

const readError = (result: any, fallback: string): string => {
  return result?.errors?.[0]?.errors?.[0] || fallback;
};

/**
 * Host Stripe Connect (Standard). Wraps StripeConnectService (ms_auth): load the
 * connection status, begin OAuth, disconnect.
 */
export const useStripeConnect = (): UseStripeConnectReturn => {
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStart, setLoadingStart] = useState(false);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const loadStatus = useCallback(
    async (refresh?: boolean): Promise<StripeConnectStatus | null> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await StripeConnectService.getStatus(refresh);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data[0] as StripeConnectStatus) || null;
          setStatus(data);
          return data;
        }

        setLastError(readError(result, "Failed to load payment connection"));
        return null;
      } catch (error) {
        setLastError(error instanceof Error ? error.message : "Failed to load payment connection");
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const start = useCallback(async (returnUrl: string): Promise<string | null> => {
    setLoadingStart(true);
    setLastError(null);

    try {
      const result = await StripeConnectService.start(returnUrl);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const url = (result.data[0] as { url?: string })?.url || null;

        if (!url) {
          setLastError("Stripe did not return a connection link");
        }

        return url;
      }

      setLastError(readError(result, "Failed to start the Stripe connection"));
      return null;
    } catch (error) {
      setLastError(error instanceof Error ? error.message : "Failed to start the Stripe connection");
      return null;
    } finally {
      setLoadingStart(false);
    }
  }, []);

  const complete = useCallback(async (code: string, state: string): Promise<StripeConnectStatus | null> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await StripeConnectService.complete(code, state);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data[0] as StripeConnectStatus) || null;
        setStatus(data);
        setLoading(false);
        return data;
      }

      setLastError(readError(result, "We couldn't finish connecting Stripe"));
    } catch (error) {
      setLastError(error instanceof Error ? error.message : "We couldn't finish connecting Stripe");
    }

    // ⚠ A refused or failed finish still shows the REAL connection (an account connected before keeps its status),
    // read without clearing the reason just set — `loadStatus` would wipe it.
    try {
      const current = await StripeConnectService.getStatus(false);

      if (current.code === OP_RESULT_CODES.OK && current.data) {
        setStatus((current.data[0] as StripeConnectStatus) || null);
      }
    } catch (error) {
      // The reason above stays on screen; an unreadable status changes nothing about it.
    } finally {
      setLoading(false);
    }

    return null;
  }, []);

  const disconnect = useCallback(async (): Promise<StripeConnectStatus | null> => {
    setLoadingDisconnect(true);
    setLastError(null);

    try {
      const result = await StripeConnectService.disconnect();

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data[0] as StripeConnectStatus) || null;
        setStatus(data);
        return data;
      }

      setLastError(readError(result, "Failed to disconnect"));
      return null;
    } catch (error) {
      setLastError(error instanceof Error ? error.message : "Failed to disconnect");
      return null;
    } finally {
      setLoadingDisconnect(false);
    }
  }, []);

  return {
    status,
    loading,
    loadingStart,
    loadingDisconnect,
    lastError,
    clearError,
    loadStatus,
    start,
    complete,
    disconnect,
  };
};
